import { Component, computed, input, linkedSignal, model, output, signal } from '@angular/core';
import { InlineButtonComponent } from '../../../../atoms/inline-button/inline-button.component';
import { DropdownOption } from '../../../../molecules/dropdown/dropdown.component';

@Component({
  selector: 'joii-date-picker-years',
  imports: [InlineButtonComponent],
  template: `
    @for (i of visibleYears(); track i.value) {
      <button
        joii-inline-button
        [variant]="'secondary'"
        [transparent]="i.value !== (year()?.value ?? 0)"
        [borderless]="i.value !== (year()?.value ?? 0)"
        [active]="i.value === (year()?.value ?? 0)"
        (click)="selectYear(i)"
      >
        {{ i.name }}
      </button>
    }
  `,
  styles: `
    :host {
      padding-top: 12px;
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 6px;
      margin-top: 4px;
    }
  `,
})
export class YearRowsComponent {
  private readonly maxRows = signal(6);
  private readonly maxColumns = signal(4);

  years = input<DropdownOption<number>[]>([]);
  year = model.required<DropdownOption<number> | undefined>();
  yearSelection = output<DropdownOption<number> | undefined>();

  currentPage = linkedSignal(() => {
    const pageSize = this.maxRows() * this.maxColumns();
    const index = this.years().findIndex((option) => option.value === this.year()?.value);
    if (index < 0) {
      return 1;
    }
    return Math.floor(index / pageSize) + 1;
  });
  totalPages = computed(() => Math.ceil(this.years().length / (this.maxRows() * this.maxColumns())));
  isFirstPage = computed(() => this.currentPage() === 1);
  isLastPage = computed(() => this.currentPage() === this.totalPages());

  visibleYears = computed(() =>
    this.years().slice(
      (this.currentPage() - 1) * this.maxRows() * this.maxColumns(),
      this.currentPage() * this.maxRows() * this.maxColumns()
    )
  );

  yearRows = computed(() => {
    const rows = [];
    for (let i = 0; i < this.visibleYears().length; i += this.maxColumns()) {
      rows.push(this.visibleYears().slice(i, i + this.maxColumns()));
    }
    return rows;
  });

  selectYear(year: DropdownOption<number>): void {
    this.year.set(year);
    this.yearSelection.emit(year);
  }

  nextPage(): void {
    const next = this.currentPage() + 1;
    if (next <= this.totalPages()) {
      this.currentPage.set(next);
    }
  }

  previousPage(): void {
    const prev = this.currentPage() - 1;
    if (prev >= 1) {
      this.currentPage.set(prev);
    }
  }
}
